<template>
    <div>

        <div class="deal-details-container" v-if="deal">
            <div class="deal-image-container">
                <a :href="(settings.isShabbat ? '#' : deal.DealURL)" target="_blank" class="button-link" id="deal-url"
                    @click="goToDeal(deal, $event)">
                    <div class="deal-image-soldout" v-if="deal.isSoldOut"><img :src="`/Assets/img/soldout.png`" /></div>
                    <img id="img-url" :src="deal.ImgUrl" :class="{ 'up-side-down': settings.purimMode }" />
                </a>
            </div>
            <div class="deal-info-container">
                <div class="deal-title">
                    <h1 id="deal-title">{{ deal.Title }}</h1>
                </div>
                <div class="deal-last-update">Last Updated {{ formatDate(deal.UpdateDate) }}</div>
                <div class="deal-desc" id="deal-desc" v-if="deal && deal.Description">
                    {{ deal.Description }}
                </div>

                <div v-if="deal.hasPrice()">
                    <div class="crossed-price"><span v-if='deal.RetailPrice'>{{ formatPrice(deal.RetailPrice) }}</span>
                    </div>
                    <div class="deal-price-container">
                        <div class="deal-price">{{ formatPrice(deal.DealPrice) }}</div>
                        <div class="deal-discount" v-if="discount">-{{ discount.toFixed(0) }}%</div>
                    </div>
                </div>


                <div class="deal-promo-code" v-if="deal.PromoCode">
                    <div class="deal-promo-code-title">Promo Code</div>
                    <div class="share-link-container">
                        <input id="promoCode" v-model="deal.PromoCode" class="promo-code-input" readonly />
                        <a v-show="!copied" class="promo-copy-code" @click="copyToClipboard">COPY</a>
                        <a v-show="copied" class="promo-copy-code copied">COPIED</a>
                    </div>

                </div>
                <div class="mini-deal-buttons">
                    <a :href="(settings.isShabbat ? '#' : deal.DealURL)" target="_blank" class="button-link" id="deal-url">
                        <button class="primary-button centered">
                            Get Deal
                        </button>
                    </a>

                    <input type="hidden" id="product-id" :value="deal.ProductID" />
                    <ShareButton :link="dealHelper.getDealLink(deal)" :messageTitle="'Check out this deal!'" :messageText="'Found this amazing deal on MommyDeals – thought you’d like it.'" />
                    <GetDealAlerts />
                </div>

                <div class="deal-amazon-img-container">
                    <DealLogoImage :deal="deal" class="logo-img" />
                </div>
                <div class="expiration-message" v-if="deal.expirationMessage">
                    {{ deal.expirationMessage }}
                </div>
                <br>
                <div id="deal-expired">
                    Disclosure: {{ settings.siteNameCaps }} is a participant in the Amazon Services LLC Associates
                    Program, an affiliate advertising program designed to provide a means for us to earn advertising
                    fees by advertising and linking to Amazon.com. As An Amazon Associate we earn from qualifying
                    purchases. Product prices and availability are accurate as of the date/time indicated and are
                    subject to change without notice including promo codes.
                </div>

            </div>
        </div>
    </div>
</template>



<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import api from '@/api'
import settings from '@/settings'
import util from '@/helpers/util'
import ga from '@/helpers/ga'
import clicky from '@/helpers/clicky'
import DealLogoImage from './DealLogoImage.vue'
import { DealDetails } from '@/classes/DealDetails'
import GetDealAlerts from './GetDealAlerts.vue'
import ShareButton from './ShareButton.vue'
import dealHelper from '@/helpers/deal-helper'

export default defineComponent({
    name: 'DealDetails',
    components: {
        DealLogoImage,
        GetDealAlerts,
        ShareButton
    },
    props: {
        dealId: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const isLoading = ref(false)
        const currentUrl = ref(window.location.href)
        const deal = ref<DealDetails | null>(null)
        const dealUrl = ref<string | null>(null)
        const isParticipant = ref<boolean | null>(null)
        const descLength = ref(80)
        const copied = ref(false)

        const settingsComputed = computed(() => settings)
        const discount = computed(() => util.getDiscountPercentage(deal.value?.DealPrice, deal.value?.RetailPrice))

        const formatDate = (date: string | Date) => {
            const d = new Date(date)
            return d.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }

        const formatPrice = (price: number): string => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(price)
        }

        const getTimeAgo = (date: string | Date) => {
            const d = new Date(date)
            const now = new Date()
            const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000)

            if (diffInSeconds < 60) return 'just now'
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
            if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
            if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
            if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
            return `${Math.floor(diffInSeconds / 31536000)} years ago`
        }

        const toCurrency = (value: number | string, decimals: number = 2) => {
            const num = typeof value === 'string' ? parseFloat(value) : value
            return num.toFixed(decimals)
        }

        const init = async () => {
            isLoading.value = true
            try {
                const response = await api.getDeal(parseInt(props.dealId))
                let tempDeal = response.data as DealDetails

                //check if an affiliate sent the traffic to us, if so, update the deal url
                let cookie_lastref = document.cookie.split(';').find(c => c.trim().startsWith('MMD_LastRef='))?.split('=')[1]

                if (settings.siteName == 'fivetowncentral' || (cookie_lastref && cookie_lastref.toLowerCase() == '5townscentral')) {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', '5townscentral-20')
                }
                else if (settings.siteName == '5tcdeals') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', '5townscentral-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', '5tcdeals')
                }
                else if (settings.siteName == 'momazon' || (cookie_lastref && cookie_lastref.toLowerCase() == 'adir')) {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'adirdeals-20')
                }
                else if (settings.siteName == 'alyssagoldwater' || (cookie_lastref && cookie_lastref.toLowerCase() == 'alyssagoldwater')) {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'agoldwater-20')
                }
                else if (cookie_lastref && cookie_lastref.toLowerCase() == 'mmdfriedman') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'mmdfriedman-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'moshe')
                }
                else if (cookie_lastref && cookie_lastref.toLowerCase() == 'klugman') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', '18ventures-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'klugman')
                }
                else if (cookie_lastref && cookie_lastref.toLowerCase() == 'dovidklugman') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'mmdbraum-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'dovidklugman')
                }
                else if (cookie_lastref && cookie_lastref.toLowerCase() == 'braum') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'mmdbraum-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'braum')
                }
                else if (cookie_lastref && cookie_lastref.toLowerCase() == 'tempelberg') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'tempelberg-20')
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'tempelberg')
                }
                else if (settings.siteName == 'mommydeals' && tempDeal.DealProvider == 'Amazon') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'mommydeals0a-20')
                }
                else if (settings.siteName == 'mommydeals' && tempDeal.DealProvider == 'Walmart') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'mommydeals')
                }
                else if (settings.siteName == 'petpetdeals' && tempDeal.DealProvider == 'Amazon') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'petpetdeals-20')
                }
                else if (settings.siteName == 'petpetdeals' && tempDeal.DealProvider == 'Walmart') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'petpetdeals')
                }
                else if (settings.siteName == 'chaideals' && tempDeal.DealProvider == 'Amazon') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'tag', 'chaideals-20')
                }
                else if (settings.siteName == 'chaideals' && tempDeal.DealProvider == 'Walmart') {
                    tempDeal.DealURL = util.replaceOrAddUrlParam(tempDeal.DealURL, 'sharedid', 'chaideals')
                }

                if (tempDeal.isExpired) {
                    tempDeal.expirationMessage = 'Note: This deal might be expired since was it was posted ' + getTimeAgo(tempDeal.UpdateDate)
                }
                else {
                    tempDeal.expirationMessage = undefined
                }

                deal.value = new DealDetails(tempDeal)
                document.title = deal.value.Title + ' | ' + settings.siteNameCaps
                loadGoogleAds()
            } finally {
                isLoading.value = false
            }
        }

        const shortDesc = computed(() => {
            if (deal.value?.Description && deal.value.Description.length > descLength.value) {
                return deal.value.Description.substring(0, descLength.value)
            }
            return deal.value?.Description
        })

        const copyToClipboard = () => {
            const copyText = document.getElementById("promoCode") as HTMLInputElement
            copyText.select()
            document.execCommand("copy")
            copied.value = true
            setTimeout(() => {
                copied.value = false
            }, 2000)
        }

        const longDesc = () => {
            if (deal.value?.Description) {
                descLength.value = deal.value.Description.length
            }
        }

        const trackOutboundLink = (url: string) => {
            ga.event('click', { 'event_category': 'outbound', 'event_label': url, 'transport_type': 'beacon' })
            clicky.goal('DealClick2')
        }

        const goToDeal = (deal: DealDetails, event: Event) => {
            if (settings.isShabbat) {
                alert(`Thank you for your patience while ${settings.siteNameCaps} breaks for the Shabbos holiday.

Deal links will resume to work on ${settings.shabbatEnds}.`)
                event.preventDefault()
            }
            else {
                trackOutboundLink(deal.DealURL)
            }
        }

        const loadMetaTags = () => {
            const arrayOfTags = []

            if (deal.value?.Title) arrayOfTags.push({ name: 'og:title', content: settings.siteNameCaps + ' | ' + deal.value.Title })
            if (deal.value?.Description) arrayOfTags.push({ name: 'og:description', content: deal.value.Description })
            if (deal.value?.ImgUrl) arrayOfTags.push({ name: 'og:image', content: deal.value.ImgUrl })

            arrayOfTags.forEach(tag => {
                const metaTag = document.createElement('meta')
                metaTag.name = tag.name
                metaTag.content = tag.content
                document.getElementsByTagName('head')[0].appendChild(metaTag)
            })
        }

        const loadGoogleAds = () => {
            //document.querySelectorAll('.adsbygoogle').forEach(function () {
            //    (window.adsbygoogle = window.adsbygoogle || []).push({});
            //});
        }

        onMounted(() => {
            init()
        })

        return {
            isLoading,
            currentUrl,
            deal,
            dealUrl,
            isParticipant,
            descLength,
            copied,
            settings: settingsComputed,
            shortDesc,
            copyToClipboard,
            longDesc,
            trackOutboundLink,
            goToDeal,
            loadMetaTags,
            loadGoogleAds,
            formatDate,
            toCurrency,
            formatPrice,
            discount,
            dealHelper
        }
    }
})
</script>

<style scoped>
.deal-price {
    font-size: 1.75em;
}

.deal-desc {
    margin-bottom: 15px;
}

.deal-info-container{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}



</style>
